/* External dependencies */
import { Row } from 'atomize';
import React, { ChangeEvent, useRef, useState } from 'react';

/* Local dependencies */
import { useDispatch, useSelector } from 'react-redux';
import formatMessage from '../../../intl/formatMessage';
import { RootState } from '../../../redux/store';
import PrimaryButton from '../../buttons/PrimaryButton';
import { convertExcelFileToArray } from '../../common/convert-excel-file-to-array';
import { GoodsPopup } from './goods-popup';
import { setCreateConsigneeGoodField } from './redux/actions';
import { Sections } from './redux/types';

const EXCEL_MIME =
  '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export function CreateConsigneeGoodSpecificationsControlBar() {
  const dispatch = useDispatch();

  const { sections: existingSections, loading } = useSelector(
    (state: RootState) => state.createConsigneeGoodsSpecification,
  );

  const [isOpenPopup, setIsOpenPopup] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  function onTogglePopup(isOpen: boolean) {
    setIsOpenPopup(isOpen);
  }

  async function onSelectFiles(e: ChangeEvent<HTMLInputElement>) {
    const { files } = e.target;

    if (files) {
      const convertedFiles = [...files];

      const queries = convertedFiles.map(async (file) => {
        const arrayFromFile = (await convertExcelFileToArray(file)) as Sections;

        const suffix = crypto.randomUUID().slice(0, 4);

        const fileName = `${file.name}-${suffix}`;
        const columnNames = Object.keys(arrayFromFile[0] || {});

        dispatch(setCreateConsigneeGoodField('files', arrayFromFile, fileName));

        return {
          name: fileName,
          values: columnNames,
        };
      });

      const newSections: Sections = await Promise.all(queries);

      const preparedSections = [...existingSections, ...newSections];

      dispatch(setCreateConsigneeGoodField('sections', preparedSections));

      /* Weed need to clear files in target 
      / because when you selected file is already selected
      / react can't trigger event
      */

      e.target.value = '';
    }
  }

  function handleOpenFileSelector() {
    fileInputRef.current?.click();
  }

  return (
    <Row
      m={{ y: '0.5rem' }}
      justify="flex-end"
      align={{ xs: 'flex-start', md: 'space-between' }}
      style={{ gap: '10px' }}
    >
      <PrimaryButton
        icon="Upload"
        type="success"
        onClick={handleOpenFileSelector}
      >
        {formatMessage('uploadFiles')}
      </PrimaryButton>

      <input
        onChange={onSelectFiles}
        style={{ display: 'none' }}
        type="file"
        accept={EXCEL_MIME}
        ref={fileInputRef}
        multiple
      />

      <PrimaryButton
        icon="Plus"
        loading={loading}
        onClick={() => onTogglePopup(true)}
      >
        {formatMessage('create')}
      </PrimaryButton>

      {isOpenPopup && <GoodsPopup onClose={() => onTogglePopup(false)} />}
    </Row>
  );
}
